import { PLAN_LEVEL_MAP, type PlanLevel } from '~/lib/.client/plans';
import type { SubscriptionPlan } from '~/types/subscription';

export interface Pricing {
  plan: SubscriptionPlan;
  name: string;
  namePrice?: string;
  level: PlanLevel;
  tier: number;
  rateMonthly: { month: number; year: number };
  buttonText?: string;
  tokens: string;
  messages: string;
  features?: string[];
  disabledForNewCustomers?: boolean;
}

const BASE_PRO_FEATURES = [
  `Unlimited file uploads`,
  `Securely connect with backends and APIs running on localhost`,
  `Access CORS-protected APIs`,
];

export const personalPricingList: Pricing[] = [
  {
    plan: 'pro',
    ...PLAN_LEVEL_MAP.pro_1,
    tier: 1,
    rateMonthly: { month: 25, year: 18 },
    tokens: '10M',
    messages: '~100',
    features: [`10M bolt.new tokens per month`, ...BASE_PRO_FEATURES],
  },
  {
    plan: 'pro',
    ...PLAN_LEVEL_MAP.pro_2,
    tier: 2,
    rateMonthly: { month: 50, year: 45 },
    tokens: '26M',
    messages: '~260',
    features: [`26M bolt.new tokens per month`, ...BASE_PRO_FEATURES],
  },
  {
    plan: 'pro',
    ...PLAN_LEVEL_MAP.pro_3,
    tier: 3,
    rateMonthly: { month: 100, year: 90 },
    tokens: '55M',
    messages: '~550',
    features: [`55M bolt.new tokens per month`, ...BASE_PRO_FEATURES],
  },
  {
    plan: 'pro',
    ...PLAN_LEVEL_MAP.pro_4,
    tier: 4,
    rateMonthly: { month: 200, year: 180 },
    tokens: '120M',
    messages: '~1200',
    features: [`120M bolt.new tokens per month`, ...BASE_PRO_FEATURES],
  },
  {
    plan: 'pro',
    ...PLAN_LEVEL_MAP.pro_5,
    tier: 5,
    rateMonthly: { month: 300, year: 270 },
    tokens: '180M',
    messages: '~1800',
    features: [`180M bolt.new tokens per month`, ...BASE_PRO_FEATURES],
  },
  {
    plan: 'pro',
    ...PLAN_LEVEL_MAP.pro_6,
    tier: 6,
    rateMonthly: { month: 400, year: 360 },
    tokens: '240M',
    messages: '~2400',
    features: [`240M bolt.new tokens per month`, ...BASE_PRO_FEATURES],
  },
  {
    plan: 'pro',
    ...PLAN_LEVEL_MAP.pro_7,
    tier: 7,
    rateMonthly: { month: 500, year: 450 },
    tokens: '300M',
    messages: '~3000',
    features: [`300M bolt.new tokens per month`, ...BASE_PRO_FEATURES],
  },
  {
    plan: 'pro',
    ...PLAN_LEVEL_MAP.pro_8,
    tier: 8,
    rateMonthly: { month: 600, year: 540 },
    tokens: '360M',
    messages: '~3600',
    features: [`360M bolt.new tokens per month`, ...BASE_PRO_FEATURES],
  },
  {
    plan: 'pro',
    ...PLAN_LEVEL_MAP.pro_9,
    tier: 9,
    rateMonthly: { month: 700, year: 630 },
    tokens: '420M',
    messages: '~4200',
    features: [`420M bolt.new tokens per month`, ...BASE_PRO_FEATURES],
  },
  {
    plan: 'pro',
    ...PLAN_LEVEL_MAP.pro_10,
    tier: 10,
    rateMonthly: { month: 800, year: 720 },
    tokens: '480M',
    messages: '~4800',
    features: [`480M bolt.new tokens per month`, ...BASE_PRO_FEATURES],
  },
  {
    plan: 'pro',
    ...PLAN_LEVEL_MAP.pro_11,
    tier: 11,
    rateMonthly: { month: 900, year: 810 },
    tokens: '540M',
    messages: '~5400',
    features: [`540M bolt.new tokens per month`, ...BASE_PRO_FEATURES],
  },
  {
    plan: 'pro',
    ...PLAN_LEVEL_MAP.pro_12,
    tier: 12,
    rateMonthly: { month: 1000, year: 900 },
    tokens: '600M',
    messages: '~6000',
    features: [`600M bolt.new tokens per month`, ...BASE_PRO_FEATURES],
  },
  {
    plan: 'pro',
    ...PLAN_LEVEL_MAP.pro_13,
    tier: 13,
    rateMonthly: { month: 1500, year: 1350 },
    tokens: '900M',
    messages: '~9000',
    features: [`900M bolt.new tokens per month`, ...BASE_PRO_FEATURES],
  },
  {
    plan: 'pro',
    ...PLAN_LEVEL_MAP.pro_14,
    tier: 14,
    rateMonthly: { month: 2000, year: 1800 },
    tokens: '1200M',
    messages: '~12000',
    features: [`1200M bolt.new tokens per month`, ...BASE_PRO_FEATURES],
  },
];

export interface MultiPricing {
  name: string;
  description: string;
  plans: Pricing[];
}

const BASE_TEAM_FEATURES = [
  `Collaborate on private projects and collections`,
  `Collaborate on your organization’s private GitHub repositories`,
  `Integrate with private NPM registries like Artifactory and Nexus`,
  `Team management and billing console`,
  `Email support`,
];

export const teamsPricingList: Pricing[] = [
  {
    plan: 'teams',
    ...PLAN_LEVEL_MAP.teams_1,
    tier: 1,
    rateMonthly: { month: 30, year: 27 },
    tokens: '10M',
    messages: '~100',
    features: [`10M bolt.new tokens per month`, ...BASE_TEAM_FEATURES],
  },
  {
    plan: 'teams',
    ...PLAN_LEVEL_MAP.teams_2,
    tier: 2,
    rateMonthly: { month: 60, year: 55 },
    tokens: '26M',
    messages: '~260',
    features: [`26M bolt.new tokens per month`, ...BASE_TEAM_FEATURES],
  },
  {
    plan: 'teams',
    ...PLAN_LEVEL_MAP.teams_3,
    tier: 3,
    rateMonthly: { month: 110, year: 100 },
    tokens: '55M',
    messages: '~550',
    features: [`55M bolt.new tokens per month`, ...BASE_TEAM_FEATURES],
  },
  {
    plan: 'teams',
    ...PLAN_LEVEL_MAP.teams_4,
    tier: 4,
    rateMonthly: { month: 200, year: 180 },
    tokens: '120M',
    messages: '~1200',
    features: [`55M bolt.new tokens per month`, ...BASE_TEAM_FEATURES],
  },
  {
    plan: 'teams',
    ...PLAN_LEVEL_MAP.teams_5,
    tier: 5,
    rateMonthly: { month: 300, year: 270 },
    tokens: '180M',
    messages: '~1800',
    features: [`180M bolt.new tokens per month`, ...BASE_TEAM_FEATURES],
  },
  {
    plan: 'teams',
    ...PLAN_LEVEL_MAP.teams_6,
    tier: 6,
    rateMonthly: { month: 400, year: 360 },
    tokens: '240M',
    messages: '~2400',
    features: [`240M bolt.new tokens per month`, ...BASE_TEAM_FEATURES],
  },
  {
    plan: 'teams',
    ...PLAN_LEVEL_MAP.teams_7,
    tier: 7,
    rateMonthly: { month: 500, year: 450 },
    tokens: '300M',
    messages: '~3000',
    features: [`300M bolt.new tokens per month`, ...BASE_TEAM_FEATURES],
  },
  {
    plan: 'teams',
    ...PLAN_LEVEL_MAP.teams_8,
    tier: 8,
    rateMonthly: { month: 600, year: 540 },
    tokens: '360M',
    messages: '~3600',
    features: [`360M bolt.new tokens per month`, ...BASE_TEAM_FEATURES],
  },
  {
    plan: 'teams',
    ...PLAN_LEVEL_MAP.teams_9,
    tier: 9,
    rateMonthly: { month: 700, year: 630 },
    tokens: '420M',
    messages: '~4200',
    features: [`420M bolt.new tokens per month`, ...BASE_TEAM_FEATURES],
  },
  {
    plan: 'teams',
    ...PLAN_LEVEL_MAP.teams_10,
    tier: 10,
    rateMonthly: { month: 800, year: 720 },
    tokens: '480M',
    messages: '~4800',
    features: [`480M bolt.new tokens per month`, ...BASE_TEAM_FEATURES],
  },
  {
    plan: 'teams',
    ...PLAN_LEVEL_MAP.teams_11,
    tier: 11,
    rateMonthly: { month: 900, year: 810 },
    tokens: '540M',
    messages: '~5400',
    features: [`540M bolt.new tokens per month`, ...BASE_TEAM_FEATURES],
  },
  {
    plan: 'teams',
    ...PLAN_LEVEL_MAP.teams_12,
    tier: 12,
    rateMonthly: { month: 1000, year: 900 },
    tokens: '600M',
    messages: '~6000',
    features: [`600M bolt.new tokens per month`, ...BASE_TEAM_FEATURES],
  },
  {
    plan: 'teams',
    ...PLAN_LEVEL_MAP.teams_13,
    tier: 13,
    rateMonthly: { month: 1500, year: 1350 },
    tokens: '900M',
    messages: '~9000',
    features: [`900M bolt.new tokens per month`, ...BASE_TEAM_FEATURES],
  },
  {
    plan: 'teams',
    ...PLAN_LEVEL_MAP.teams_14,
    tier: 14,
    rateMonthly: { month: 2000, year: 1800 },
    tokens: '1200M',
    messages: '~12000',
    features: [`1200M bolt.new tokens per month`, ...BASE_TEAM_FEATURES],
  },
];
